import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { userService, User } from '../services/userService';
import LoadingSpinner from './LoadingSpinner';

interface UserLookupProps {
    onSelect: (user: User) => void;
    excludeUserIds?: number[]; // Users to exclude from results
    placeholder?: string;
    minChars?: number;
}

const UserLookup: React.FC<UserLookupProps> = ({
    onSelect,
    excludeUserIds = [],
    placeholder = 'Search by name or email...',
    minChars = 2,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [showResults, setShowResults] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

    // Debounced search query
    const [debouncedQuery, setDebouncedQuery] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, 300); // 300ms debounce
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch users when debounced query is long enough
    const { data, isLoading, error } = useQuery({
        queryKey: ['userLookup', debouncedQuery],
        queryFn: () => userService.getUsers({ search: debouncedQuery, limit: 10 }),
        enabled: debouncedQuery.length >= minChars,
        staleTime: 0,
    });

    const users = data?.users || [];

    // Filter out excluded users
    const filteredUsers = users.filter(
        (user) => !excludeUserIds.includes(user.id)
    );

    // Close results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                resultsRef.current &&
                !resultsRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setShowResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectUser = (user: User) => {
        onSelect(user);
        setSearchQuery('');
        setShowResults(false);
    };

    return (
        <div className="relative w-full">
            <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowResults(e.target.value.length >= minChars);
                    }}
                    onFocus={() => {
                        if (searchQuery.length >= minChars) {
                            setShowResults(true);
                        }
                    }}
                    placeholder={placeholder}
                    className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                {searchQuery && (
                    <button
                        onClick={() => {
                            setSearchQuery('');
                            setShowResults(false);
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                )}
            </div>

            {showResults && searchQuery.length >= minChars && (
                <div
                    ref={resultsRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                >
                    {isLoading ? (
                        <div className="p-4 text-center">
                            <LoadingSpinner size="sm" />
                        </div>
                    ) : filteredUsers.length > 0 ? (
                        <ul className="divide-y divide-gray-100">
                            {filteredUsers.map((user) => (
                                <li
                                    key={user.id}
                                    onClick={() => handleSelectUser(user)}
                                    className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                                >
                                    <div className="flex justify-between">
                                        <div>
                                            <div className="font-medium text-gray-900">
                                                {user.firstName} {user.lastName}
                                            </div>
                                            <div className="text-sm text-gray-500">{user.email}</div>
                                        </div>
                                        <div className="text-xs text-gray-400 self-center">
                                            {user.role}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : error ? (
                        <div className="p-4 text-center text-red-500 text-sm">
                            Failed to load users
                        </div>
                    ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            No users found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserLookup;
